import React, { useState, useEffect } from 'react';

const PartForm = ({ part, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    reference: '',
    category: '',
    supplier: '',
    cost_price: '',
    selling_price: '',
    stock_quantity: '',
    min_stock_alert: '',
    location: '',
    barcode: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (part) {
      setFormData({
        name: part.name || '',
        reference: part.reference || '',
        category: part.category || '',
        supplier: part.supplier || '',
        cost_price: part.cost_price || '',
        selling_price: part.selling_price || part.price || '',
        stock_quantity: part.stock_quantity || part.stock_qty || '',
        min_stock_alert: part.min_stock_alert || '',
        location: part.location || '',
        barcode: part.barcode || ''
      });
    }
  }, [part]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Le nom est requis';
    if (formData.cost_price && isNaN(parseFloat(formData.cost_price))) newErrors.cost_price = "Le prix d'achat doit être un nombre";
    if (formData.selling_price && isNaN(parseFloat(formData.selling_price))) newErrors.selling_price = 'Le prix de vente doit être un nombre';
    if (formData.stock_quantity && isNaN(parseInt(formData.stock_quantity))) newErrors.stock_quantity = 'La quantité doit être un nombre entier';
    if (formData.min_stock_alert && isNaN(parseInt(formData.min_stock_alert))) newErrors.min_stock_alert = "Le seuil d'alerte doit être un nombre entier";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const dataToSave = {
      name: formData.name,
      reference: formData.reference || '',
      category: formData.category || '',
      supplier: formData.supplier || '',
      cost_price: formData.cost_price ? parseFloat(formData.cost_price) : null,
      price: formData.selling_price ? parseFloat(formData.selling_price) : (formData.price ? parseFloat(formData.price) : null),
      stock_qty: formData.stock_quantity ? parseInt(formData.stock_quantity) : 0,
      min_stock_alert: formData.min_stock_alert ? parseInt(formData.min_stock_alert) : 0,
      location: formData.location || '',
      barcode: formData.barcode || ''
    };
    try {
      await onSave(dataToSave);
    } catch (e) {
      console.error('Erreur lors de la sauvegarde:', e);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  return (
    <div className="part-form-overlay">
      <div className="part-form-container">
        <div className="part-form-header">
          <h2>{part ? 'Modifier le produit' : 'Nouveau produit'}</h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="part-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">Nom du produit *</label>
              <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={errors.name ? 'error' : ''} placeholder="Ex: Hébergement annuel, Pack SEO, Maintenance WordPress" />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="reference">Référence interne</label>
              <input type="text" id="reference" name="reference" value={formData.reference} onChange={handleChange} placeholder="Ex: PROD-SEO-001" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="category">Catégorie</label>
              <select id="category" name="category" value={formData.category} onChange={handleChange}>
                <option value="">Sélectionner une catégorie</option>
                <option value="Hébergement">Hébergement</option>
                <option value="Maintenance">Maintenance</option>
                <option value="SEO">SEO</option>
                <option value="SEA">SEA</option>
                <option value="Rédaction">Rédaction</option>
                <option value="Design">Design</option>
                <option value="Développement">Développement</option>
                <option value="Formation">Formation</option>
                <option value="Licence">Licence</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="supplier">Fournisseur / Éditeur</label>
              <input type="text" id="supplier" name="supplier" value={formData.supplier} onChange={handleChange} placeholder="Ex: OVH, Google, Semrush..." />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="cost_price">Coût d'achat (€)</label>
              <input type="number" step="0.01" id="cost_price" name="cost_price" value={formData.cost_price} onChange={handleChange} onWheel={(e)=>e.currentTarget.blur()} inputMode="decimal" className={errors.cost_price ? 'error' : ''} placeholder="0.00" />
              {errors.cost_price && <span className="error-text">{errors.cost_price}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="selling_price">Prix de vente (€)</label>
              <input type="number" step="0.01" id="selling_price" name="selling_price" value={formData.selling_price} onChange={handleChange} onWheel={(e)=>e.currentTarget.blur()} inputMode="decimal" className={errors.selling_price ? 'error' : ''} placeholder="0.00" />
              {errors.selling_price && <span className="error-text">{errors.selling_price}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="stock_quantity">Stock disponible</label>
              <input type="number" id="stock_quantity" name="stock_quantity" value={formData.stock_quantity} onChange={handleChange} className={errors.stock_quantity ? 'error' : ''} placeholder="0 (laisser vide pour services)" />
              {errors.stock_quantity && <span className="error-text">{errors.stock_quantity}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="min_stock_alert">Seuil d'alerte stock</label>
              <input type="number" id="min_stock_alert" name="min_stock_alert" value={formData.min_stock_alert} onChange={handleChange} className={errors.min_stock_alert ? 'error' : ''} placeholder="5 (laisser vide pour services)" />
              {errors.min_stock_alert && <span className="error-text">{errors.min_stock_alert}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="location">Emplacement (si stock)</label>
              <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} placeholder="Ex: Interne / Étagère A-1" />
            </div>
            <div className="form-group">
              <label htmlFor="barcode">Code interne (SKU / Code-barres)</label>
              <input type="text" id="barcode" name="barcode" value={formData.barcode} onChange={handleChange} placeholder="Ex: SKU-2025-001" />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">Annuler</button>
            <button type="submit" disabled={loading} className="btn-submit">{loading ? 'Sauvegarde...' : (part ? 'Mettre à jour le produit' : 'Créer le produit')}</button>
          </div>
        </form>

        <style>{`
          .part-form-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 20px; }
          .part-form-container { background: var(--admin-panel); color: var(--admin-text); border: 1px solid var(--admin-border); border-radius: 12px; width: 100%; max-width: 900px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 35px -5px rgba(0,0,0,0.35); }
          .part-form-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 20px 0 20px; border-bottom: 1px solid var(--admin-border); margin-bottom: 16px; }
          .part-form-header h2 { margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--admin-text); }
          .close-btn { background: none; border: 1px solid transparent; color: var(--admin-muted); cursor: pointer; padding: 6px; border-radius: 6px; transition: all .2s ease; }
          .close-btn:hover { background: rgba(255,255,255,0.06); border-color: var(--admin-border); color: var(--admin-text); }
          .part-form { padding: 20px 24px 24px 24px; }
          .form-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px,1fr)); gap: 20px; margin-bottom: 20px; }
          .form-group { display: flex; flex-direction: column; gap: 8px; }
          .form-group label { font-weight: 500; color: var(--admin-text); font-size: .9rem; }
          .form-group input, .form-group select, .form-group textarea { padding: 12px; border: 1px solid var(--admin-border); background: var(--admin-panel-2); color: var(--admin-text); border-radius: 8px; font-size: .9rem; transition: border-color .2s ease, box-shadow .2s ease; }
          .form-group input:focus, .form-group select:focus, .form-group textarea:focus { outline: none; border-color: var(--admin-accent); box-shadow: 0 0 0 3px rgba(79,70,229,0.20); }
          .form-group input.error, .form-group select.error, .form-group textarea.error { border-color: #ef4444; }
          .error-text { color: #ef4444; font-size: .8rem; margin-top: 2px; }
          .form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--admin-border); }
          .btn-cancel { background: var(--admin-panel-2); color: var(--admin-text); border: 1px solid var(--admin-border); padding: 10px 16px; border-radius: 8px; cursor: pointer; }
          .btn-cancel:hover { background: rgba(255,255,255,0.06); }
          .btn-submit { display: inline-flex; align-items: center; gap: 10px; padding: 10px 16px; border-radius: 10px; background: linear-gradient(135deg, var(--admin-accent), rgba(79,70,229,0.85)); border: 1px solid rgba(79,70,229,0.6); box-shadow: 0 6px 18px rgba(79,70,229,0.35); color: white; cursor: pointer; }
          .btn-submit:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.04); }
          .btn-submit:active:not(:disabled) { transform: translateY(0); filter: brightness(0.98); }
          .btn-submit:disabled { opacity: .6; cursor: not-allowed; }
          @media (max-width: 768px) { .part-form-overlay { padding: 10px; } .part-form-container { max-width: 100%; } .form-actions { flex-direction: column; align-items: stretch; } }
        `}</style>
      </div>
    </div>
  );
};

export default PartForm;
